import { useState } from "react"
import { Input } from "@/components/ui/input"
import { useQuery } from "@tanstack/react-query"
import { UpcomingEventsTable } from "@/components/dashboard/UpcomingEventsTable"
import { getUpcomingHostEvents } from "@/lib/queries/events"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

export default function HostEvents({ profile, session }: { profile: any | null, session: any | null }) {
  const [searchQuery, setSearchQuery] = useState("")

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["host-events", profile?.id],
    queryFn: () => getUpcomingHostEvents(session?.access_token!, 10),
    enabled: !!profile?.id && !!session?.access_token,
  })

  const upcomingEvents = events.filter(
    (event) => 
      event.status !== "event_complete" && 
      event.status !== "event_cancelled" &&
      event.status !== "payment_failed" &&
      (event.event_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (event.location && event.location.toLowerCase().includes(searchQuery.toLowerCase())))
  )

  const completedEvents = events.filter(
    (event) => 
      event.status === "event_complete" &&
      (event.event_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (event.location && event.location.toLowerCase().includes(searchQuery.toLowerCase())))
  )

  const cancelledEvents = events.filter(
    (event) => 
      (event.status === "event_cancelled" || event.status === "payment_failed") &&
      (event.event_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (event.location && event.location.toLowerCase().includes(searchQuery.toLowerCase())))
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Events</h1>
      </div>

      {events.length > 2 && (
        <div className="flex items-center space-x-2">
          <Input
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
        </div>
      )}

      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList>
          <TabsTrigger value="upcoming">
            Upcoming Events {upcomingEvents.length > 0 && `(${upcomingEvents.length})`}
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed Events {completedEvents.length > 0 && `(${completedEvents.length})`}
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            Cancelled Events {cancelledEvents.length > 0 && `(${cancelledEvents.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-4">
          {upcomingEvents.length > 0 ? (
            <UpcomingEventsTable events={upcomingEvents} />
          ) : (
            <div className="text-center text-muted-foreground">
              {searchQuery
                ? "No events match your search criteria."
                : "No upcoming events found."}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedEvents.length > 0 ? (
            <UpcomingEventsTable events={completedEvents} />
          ) : (
            <div className="text-center text-muted-foreground">
              {searchQuery
                ? "No events match your search criteria."
                : "No completed events found."}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="space-y-4">
          {cancelledEvents.length > 0 ? (
            <UpcomingEventsTable events={cancelledEvents} />
          ) : (
            <div className="text-center text-muted-foreground">
              {searchQuery
                ? "No events match your search criteria."
                : "No cancelled events found."}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
